/**
 * Firebase Authentication service
 */

import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { firebaseAuth } from './firebase.js';

class AuthService {
  constructor() {
    this.user = null;
    this._readyPromise = null;
    this._readyResolve = null;
  }

  /**
   * Initialize auth and return when first auth state is known
   */
  init() {
    if (this._readyPromise) return this._readyPromise;

    this._readyPromise = new Promise((resolve) => {
      this._readyResolve = resolve;
    });

    onAuthStateChanged(firebaseAuth, (user) => {
      this.user = user;

      if (this._readyResolve) {
        this._readyResolve(!!user);
        this._readyResolve = null;
      }

      if (user) {
        window.dispatchEvent(new CustomEvent('auth-success', {
          detail: { user, email: user.email, uid: user.uid },
        }));
      } else {
        window.dispatchEvent(new CustomEvent('auth-signout'));
      }
    });

    return this._readyPromise;
  }

  async signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(firebaseAuth, provider);
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        window.dispatchEvent(new CustomEvent('auth-error', {
          detail: { error: 'Sign-in cancelled' },
        }));
      } else if (error.code === 'auth/popup-blocked') {
        window.dispatchEvent(new CustomEvent('auth-error', {
          detail: { error: 'Pop-up blocked. Please allow pop-ups for this site.' },
        }));
      } else {
        window.dispatchEvent(new CustomEvent('auth-error', {
          detail: { error: error.message },
        }));
      }
    }
  }

  async signOut() {
    await firebaseSignOut(firebaseAuth);
    this.user = null;
  }

  isAuthenticated() {
    return !!this.user;
  }

  getUserId() {
    return this.user?.uid ?? null;
  }

  getUserEmail() {
    return this.user?.email ?? null;
  }
}

export const auth = new AuthService();
